/**
 * ActivityDetailPage - Redesigned
 * 
 * Modern activity detail view with:
 * - Integrated map hero section
 * - Key metrics grid
 * - Dynamic stacked performance charts
 * - Zone distribution visualization
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Paper,
  Divider,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Collapse,
  alpha,
  useTheme,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  OpenInNew,
  Close,
  Lightbulb as SuggestionIcon,
  BarChart as ChartIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useGetActivityByIdQuery, useGetWorkoutSegmentsQuery, useGetHeartRateZonesQuery, useGetPaceZonesQuery } from '../../../store/api/baseApi';
import { MapHeroSection } from '../components/MapHeroSection';
import { KeyMetricsGrid } from '../components/KeyMetricsGrid';
import { WorkoutAwareChart } from '../components/WorkoutAwareChart';
import { ZoneDistributionChart } from '../components/ZoneDistributionChart';
import { WorkoutLinkButton } from '../components/WorkoutLinkButton';
import { SegmentCard } from '../components/workout-segments/SegmentCard';
import { SegmentDetailView } from '../components/workout-segments/SegmentDetailView';
import type { WorkoutSegmentResponse, WorkoutSegmentDetailResponse } from '../../../store/api/models/workoutModels';
import type { RootState } from '../../../store';
import {
  getIntensityScoreInterpretation,
  getWorkoutAbovePctInterpretation,
  getWorkoutBelowPctInterpretation,
} from '../utils/intensityInterpretation';
import { formatDuration } from '../utils/formatters';

/**
 * ActivityDetailPage Component
 */
export const ActivityDetailPage: React.FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const [selectedSegment, setSelectedSegment] = useState<WorkoutSegmentResponse | null>(null);
  const [segmentDetails, setSegmentDetails] = useState<WorkoutSegmentDetailResponse[]>([]);
  const [isLoadingSegmentDetails, setIsLoadingSegmentDetails] = useState(false);
  const [isWorkoutCollapsed, setIsWorkoutCollapsed] = useState(false);
  const [showWorkoutAnalysis, setShowWorkoutAnalysis] = useState(false);
  const theme = useTheme();

  // Fetch activity data with streams
  const {
    data: activity,
    isLoading,
    isError,
    error,
  } = useGetActivityByIdQuery(
    {
      activity_id: Number(activityId),
      include_streams: true,
      res: '5s'
    },
    {
      skip: !activityId || isNaN(Number(activityId)),
    }
  );

  // Fetch workout segments if activity has a linked workout
  // Note: Must use skip to handle conditional fetching while maintaining hook order
  const {
    data: workoutSegmentsData,
    isLoading: isLoadingSegments,
  } = useGetWorkoutSegmentsQuery(
    {
      workoutId: activity?.workout_id ?? 0,
      activityId: Number(activityId) || 0,
    },
    {
      skip: !activityId || isNaN(Number(activityId)) || !activity?.workout_id,
    }
  );

  const segments = workoutSegmentsData?.segments || [];

  // Fetch user's heart rate and pace zones for zone distribution visualization
  const { data: hrZonesData } = useGetHeartRateZonesQuery();
  const { data: paceZonesData } = useGetPaceZonesQuery();

  // Transform zones to match ZoneDistributionChart expected format
  const heartRateZones = React.useMemo(() => {
    if (!hrZonesData?.zones) return [];
    return hrZonesData.zones.map(zone => ({
      zone: zone.zone,
      min_bpm: zone.min_hr,
      max_bpm: zone.max_hr,
      name: zone.name,
      description: '',
    }));
  }, [hrZonesData]);

  const paceZones = React.useMemo(() => {
    if (!paceZonesData?.zones) return [];
    return paceZonesData.zones.map(zone => ({
      zone: zone.zone,
      min_pace: zone.min_pace,
      max_pace: zone.max_pace,
      name: zone.name,
      description: '',
    }));
  }, [paceZonesData]);

  // Fetch segment details for workout visualization
  useEffect(() => {
    if (!segments || segments.length === 0 || !token) {
      setSegmentDetails([]);
      return;
    }

    const fetchAllSegments = async () => {
      setIsLoadingSegmentDetails(true);

      try {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

        const fetchPromises = segments.map(async (segment) => {
          const response = await fetch(
            `${API_BASE_URL}/segments/${segment.id}?sampling_rate=2`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch segment ${segment.id}`);
          }

          return response.json() as Promise<WorkoutSegmentDetailResponse>;
        });

        const results = await Promise.all(fetchPromises);
        setSegmentDetails(results);
      } catch (error) {
        console.error('Error fetching segment details:', error);
        setSegmentDetails([]);
      } finally {
        setIsLoadingSegmentDetails(false);
      }
    };

    fetchAllSegments();
  }, [segments, token]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/athlete/activities');
  };

  // Handle view on Strava
  const handleViewOnStrava = () => {
    if (activity?.strava_id) {
      window.open(`https://www.strava.com/activities/${activity.strava_id}`, '_blank');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <CircularProgress size={64} sx={{ color: '#ff9800 !important' }} />
          <Typography variant="body2" color="text.secondary">
            Loading activity...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (isError || !activity) {
    console.log(!activity);
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Go Back
            </Button>
          }
        >
          {error && 'status' in error
            ? `Error ${error.status}: Failed to load activity`
            : 'Activity not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2 }}
          >
            {/* Back button and title */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={handleBack} size="small">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h6" component="h1">
                  {activity.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(new Date(activity.start_date_local), 'PPP p')}
                </Typography>
              </Box>
            </Stack>

            {/* Actions */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                startIcon={<OpenInNew />}
                onClick={handleViewOnStrava}
              >
                View on Strava
              </Button>
              <WorkoutLinkButton
                activityId={activity.id}
                workoutId={activity.workout_id}
                size="medium"
              />
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Map Hero Section */}
      <MapHeroSection
        activity={activity}
        isLoading={false}
        initialCollapsed={true}
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={3}>
          {/* Key Metrics Grid */}
          <Box>
            <KeyMetricsGrid
              activity={activity}
              isLoading={false}
            />
          </Box>

          {/* Dynamic Performance Charts with Workout Target Zones */}
          <Paper sx={{ p: 3 }}>
            <WorkoutAwareChart
              streams={activity.streams || null}
              workoutSegments={segmentDetails}
              isLoading={isLoadingSegmentDetails}
              xAxisType="time"
              initialCollapsed={false}
              sportType={activity.sport_type}
              activityName={activity.name}
              polyline={activity.maps?.polyline || activity.maps?.summary_polyline}
              lactateThresholdHr={activity.zone_metrics?.lactate_threshold_hr || null}
            />
          </Paper>

          {/* Workout-Level Intensity Summary - Show if activity has linked workout with aggregate data */}
          {activity.workout_id && workoutSegmentsData && (
            workoutSegmentsData.overall_workout_intensity_score !== null ||
            workoutSegmentsData.overall_workout_compliance_score !== null
          ) && (() => {
            const workoutIntensityInterpretation = getIntensityScoreInterpretation(
              workoutSegmentsData.overall_workout_intensity_score
            );
            const abovePctInterpretation = getWorkoutAbovePctInterpretation(
              workoutSegmentsData.overall_workout_above_pct
            );
            const belowPctInterpretation = getWorkoutBelowPctInterpretation(
              workoutSegmentsData.overall_workout_below_pct
            );

            const hasSuggestions = workoutIntensityInterpretation.message || abovePctInterpretation || belowPctInterpretation;

            // Check if there are any left column metrics to display
            const hasLeftColumnMetrics =
              workoutSegmentsData.overall_workout_intensity_score !== null ||
              workoutSegmentsData.overall_workout_compliance_score !== null ||
              (workoutSegmentsData.overall_workout_above_pct !== null && workoutSegmentsData.overall_workout_above_pct > 0) ||
              (workoutSegmentsData.overall_workout_below_pct !== null && workoutSegmentsData.overall_workout_below_pct > 0);

            // Get the compliance color for the header
            const getComplianceColor = (score?: number | null): string => {
              if (!score) return '#9e9e9e';
              if (score >= 0.8) return '#66bb6a';
              if (score >= 0.6) return '#ffa726';
              return '#ef5350';
            };

            const complianceColor = getComplianceColor(workoutSegmentsData.overall_workout_compliance_score);

            // Helper for Metric Cards
            const MetricCard = ({ title, value, color, severity, tooltip }: any) => (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  bgcolor: alpha(color, 0.04),
                  borderColor: alpha(color, 0.3),
                  transition: 'transform 0.2s',
                  minHeight: 222,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: color,
                  }
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {title}
                  </Typography>
                  {tooltip && (
                    <Tooltip title={tooltip} arrow placement="top">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', '&:hover': { color: 'text.secondary' } }} />
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ color: color }}>
                    {value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} mt={0.5}>
                    {severity}
                  </Typography>
                </Box>
              </Paper>
            );

            // Calculate time distribution percentages
            const targetZonesTotal = (workoutSegmentsData.total_time_in_target_seconds || 0) +
              (workoutSegmentsData.total_time_above_target_seconds || 0) +
              (workoutSegmentsData.total_time_below_target_seconds || 0);

            const getTargetPct = (seconds?: number | null) => {
              if (!seconds || !targetZonesTotal) return 0;
              return (seconds / targetZonesTotal) * 100;
            };

            const inTargetPct = getTargetPct(workoutSegmentsData.total_time_in_target_seconds);
            const aboveTargetPct = getTargetPct(workoutSegmentsData.total_time_above_target_seconds);
            const belowTargetPct = getTargetPct(workoutSegmentsData.total_time_below_target_seconds);

            return (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Workout Performance Summary
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3} alignItems="stretch">
                  {/* LEFT COLUMN: 2x2 Grid - Only render if metrics exist */}
                  {hasLeftColumnMetrics && (
                    <Grid item xs={12} md={7}>
                      <Grid container spacing={2} sx={{ height: '100%' }}>
                        {/* 1. Overall Intensity Score */}
                        {workoutSegmentsData.overall_workout_intensity_score !== null && (
                          <Grid item xs={12} sm={6}>
                            <MetricCard
                              title="Overall Intensity"
                              value={`${(workoutSegmentsData.overall_workout_intensity_score * 100).toFixed(0)}%`}
                              color={workoutIntensityInterpretation.color}
                              severity={workoutIntensityInterpretation.label}
                              tooltip="Comprehensive intensity score for the entire workout."
                            />
                          </Grid>
                        )}

                        {/* 2. Training Stress Score / Time in Target */}
                        {(activity.zone_metrics?.training_stress_score !== undefined || workoutSegmentsData.overall_workout_compliance_score !== null) && (
                          <Grid item xs={12} sm={6}>
                            {activity.zone_metrics?.training_stress_score !== undefined ? (
                              <MetricCard
                                title="Training Stress Score"
                                value={activity.zone_metrics.training_stress_score.toFixed(1)}
                                color="#8e24aa"
                                severity="Intensity Load"
                                tooltip="Quantifies the training load of this activity based on zone metrics."
                              />
                            ) : (
                              <MetricCard
                                title="Time in Target"
                                value={`${(workoutSegmentsData.overall_workout_compliance_score! * 100).toFixed(0)}%`}
                                color={complianceColor}
                                severity={formatDuration(workoutSegmentsData.total_time_in_target_seconds || 0)}
                                tooltip="Percentage of time spent within the programmed target intensity range."
                              />
                            )}
                          </Grid>
                        )}

                        {/* 3. Too Intense */}
                        {workoutSegmentsData.overall_workout_above_pct !== null && workoutSegmentsData.overall_workout_above_pct > 0 && (
                          <Grid item xs={12} sm={6}>
                            <MetricCard
                              title="Too Intense"
                              value={`${workoutSegmentsData.overall_workout_above_pct.toFixed(0)}%`}
                              color="#ffa726"
                              severity={formatDuration(workoutSegmentsData.total_time_above_target_seconds || 0)}
                              tooltip="Time spent above the target intensity zone."
                            />
                          </Grid>
                        )}

                        {/* 4. Not Intense Enough */}
                        {workoutSegmentsData.overall_workout_below_pct !== null && workoutSegmentsData.overall_workout_below_pct > 0 && (
                          <Grid item xs={12} sm={6}>
                            <MetricCard
                              title="Not Intense Enough"
                              value={`${workoutSegmentsData.overall_workout_below_pct.toFixed(0)}%`}
                              color="#42a5f5"
                              severity={formatDuration(workoutSegmentsData.total_time_below_target_seconds || 0)}
                              tooltip="Time spent below the target intensity zone."
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}

                  {/* RIGHT COLUMN: Toggleable Statistics/Analysis Panel */}
                  <Grid item xs={12} md={hasLeftColumnMetrics ? 5 : 12}>
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{
                        height: 461,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        borderColor: 'divider',
                      }}
                    >
                      {/* Panel Header with Toggle */}
                      <Box
                        sx={{
                          p: 2,
                          px: 3,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          bgcolor: alpha(complianceColor, 0.15),
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {showWorkoutAnalysis ? <SuggestionIcon sx={{ color: complianceColor }} fontSize="small" /> : <ChartIcon sx={{ color: complianceColor }} fontSize="small" />}
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: complianceColor }}>
                            {showWorkoutAnalysis ? 'Analysis & Suggestions' : 'Consistency Overview'}
                          </Typography>
                        </Stack>

                        {hasSuggestions && (
                          <Tooltip title={showWorkoutAnalysis ? "Show Statistics" : "Show Analysis"}>
                            <IconButton
                              size="small"
                              onClick={() => setShowWorkoutAnalysis(!showWorkoutAnalysis)}
                              sx={{
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              {showWorkoutAnalysis ? <ChartIcon fontSize="small" /> : <SuggestionIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Panel Content Area */}
                      <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'auto' }}>
                        {showWorkoutAnalysis ? (
                          // VIEW A: ANALYSIS & SUGGESTIONS
                          <Stack spacing={2} sx={{ width: '100%', height: '100%', justifyContent: 'flex-start' }}>
                            {workoutIntensityInterpretation.message && (
                              <Alert severity={workoutIntensityInterpretation.level === 'excellent' ? 'success' : workoutIntensityInterpretation.level === 'good' ? 'info' : workoutIntensityInterpretation.level === 'fair' ? 'warning' : 'error'} icon={false} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Overall: {workoutIntensityInterpretation.message}</Typography>
                                {workoutIntensityInterpretation.description && <Typography variant="caption" display="block">{workoutIntensityInterpretation.description}</Typography>}
                              </Alert>
                            )}
                            {abovePctInterpretation && workoutSegmentsData.overall_workout_above_pct && workoutSegmentsData.overall_workout_above_pct > 10 && (
                              <Alert severity="warning" icon={false} sx={{ border: '1px solid', borderColor: 'warning.light' }}>
                                <Typography variant="subtitle2" fontWeight="bold">When too intense: {abovePctInterpretation.message}</Typography>
                                <Typography variant="caption" display="block">You spent {workoutSegmentsData.overall_workout_above_pct.toFixed(1)}% of the workout above target intensity. Consider better pacing strategies to avoid overtraining.</Typography>
                              </Alert>
                            )}
                            {belowPctInterpretation && workoutSegmentsData.overall_workout_below_pct && workoutSegmentsData.overall_workout_below_pct > 10 && (
                              <Alert severity="info" icon={false} sx={{ border: '1px solid', borderColor: 'info.light' }}>
                                <Typography variant="subtitle2" fontWeight="bold">When not intense enough: {belowPctInterpretation.message}</Typography>
                                <Typography variant="caption" display="block">You spent {workoutSegmentsData.overall_workout_below_pct.toFixed(1)}% of the workout below target intensity. Push harder next time for better training adaptations.</Typography>
                              </Alert>
                            )}
                            {!workoutIntensityInterpretation.message && (!abovePctInterpretation || !workoutSegmentsData.overall_workout_above_pct || workoutSegmentsData.overall_workout_above_pct <= 10) && (!belowPctInterpretation || !workoutSegmentsData.overall_workout_below_pct || workoutSegmentsData.overall_workout_below_pct <= 10) && (
                              <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="text.secondary">No major suggestions derived from this data.</Typography>
                              </Box>
                            )}
                          </Stack>
                        ) : (
                          // VIEW B: STATS (Consistency + Time Dist)
                          <Box sx={{ width: '100%' }}>
                            <Grid container spacing={hasLeftColumnMetrics ? 0 : 4} alignItems="center">
                              {/* Overall Compliance Score */}
                              {workoutSegmentsData.overall_workout_compliance_score !== null && (
                                <Grid item xs={12} md={hasLeftColumnMetrics ? 12 : 4}>
                                  <Box textAlign="center" mb={hasLeftColumnMetrics ? 3 : 0}>
                                    <Box position="relative" display="inline-flex" justifyContent="center">
                                      <CircularProgress
                                        variant="determinate"
                                        value={100}
                                        size={100}
                                        thickness={3}
                                        sx={{ color: theme.palette.action.hover }}
                                      />
                                      <CircularProgress
                                        variant="determinate"
                                        value={workoutSegmentsData.overall_workout_compliance_score * 100}
                                        size={100}
                                        thickness={3}
                                        sx={{
                                          color: complianceColor,
                                          '& .MuiCircularProgress-circle': {
                                            stroke: complianceColor,
                                          },
                                          position: 'absolute',
                                          left: 0,
                                          strokeLinecap: 'round',
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          top: 0,
                                          left: 0,
                                          bottom: 0,
                                          right: 0,
                                          position: 'absolute',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>
                                          {(workoutSegmentsData.overall_workout_compliance_score * 100).toFixed(0)}
                                          <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>%</Typography>
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500} mt={1}>
                                      Target Execution Score
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}

                              {hasLeftColumnMetrics && <Grid item xs={12}><Divider sx={{ mb: 2.5 }} /></Grid>}

                              {/* Time Distribution Stack */}
                              <Grid item xs={12} md={hasLeftColumnMetrics ? 12 : 8}>
                                <Box>
                                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" mb={2} display="block">
                                    Time Distribution
                                  </Typography>
                                  <Stack spacing={2}>
                                    {/* In Target */}
                                    <Box>
                                      <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                        <Typography variant="caption" fontWeight={600} color="text.primary">In Target</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatDuration(workoutSegmentsData.total_time_in_target_seconds)}</Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={inTargetPct}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: alpha(theme.palette.success.main, 0.1),
                                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.main, borderRadius: 3 }
                                        }}
                                      />
                                    </Box>

                                    {/* Above Target */}
                                    <Box>
                                      <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                        <Typography variant="caption" fontWeight={600} color="text.primary">Above Target</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatDuration(workoutSegmentsData.total_time_above_target_seconds)}</Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={aboveTargetPct}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: alpha(theme.palette.error.main, 0.1),
                                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.error.main, borderRadius: 3 }
                                        }}
                                      />
                                    </Box>

                                    {/* Below Target */}
                                    <Box>
                                      <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                        <Typography variant="caption" fontWeight={600} color="text.primary">Below Target</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatDuration(workoutSegmentsData.total_time_below_target_seconds)}</Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={belowTargetPct}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: alpha(theme.palette.info.main, 0.1),
                                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.info.main, borderRadius: 3 }
                                        }}
                                      />
                                    </Box>
                                  </Stack>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            );
          })()}

          {/* Workout Segments Cards - Show if activity has linked workout */}
          {activity.workout_id && segments.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: isWorkoutCollapsed ? 0 : 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Workout Segments
                    </Typography>
                    {!isWorkoutCollapsed && (
                      <Typography variant="body2" color="text.secondary">
                        Click on any segment to view detailed analysis
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  onClick={() => setIsWorkoutCollapsed(!isWorkoutCollapsed)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: alpha(theme.palette.action.hover, 0.05),
                    },
                  }}
                >
                  <Typography variant="caption">
                    {isWorkoutCollapsed ? 'Expand' : 'Collapse'}
                  </Typography>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform: isWorkoutCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 200ms',
                    }}
                  >
                    <path
                      d="M7 14L12 9L17 14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              </Box>

              <Collapse in={!isWorkoutCollapsed} timeout={300}>
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={2}>
                    {segments.map((segment) => (
                      <Grid item xs={12} sm={6} md={4} key={segment.id}>
                        <SegmentCard
                          segment={segment}
                          onClick={() => setSelectedSegment(segment)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Zone Distribution */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Zone Distribution
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ZoneDistributionChart
              streams={activity.streams}
              zoneMetrics={activity.zone_metrics}
              heartRateZones={heartRateZones}
              paceZones={paceZones}
              isLoading={false}
              showAdvancedMetrics={true}
            />
          </Paper>
        </Stack>
      </Container>

      {/* Segment Detail Dialog */}
      <Dialog
        open={!!selectedSegment}
        onClose={() => setSelectedSegment(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Segment Detail
            </Typography>
            <IconButton onClick={() => setSelectedSegment(null)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSegment && (
            <SegmentDetailView
              segment={selectedSegment}
              onBack={() => setSelectedSegment(null)}
              sportType={activity.sport_type}
              segmentDetail={segmentDetails.find(sd => sd.id === selectedSegment.id)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ActivityDetailPage;
